import { FootNoteLink } from '@massds/mayflower-react';
import { ReactTableDefaults } from 'react-table';
import ReactDOMServer from 'react-dom/server';
import React from 'react';
import sum from 'lodash.sum';
import cloneDeep from 'lodash.clonedeep';
import numbro from 'numbro';
import DangerousWrapper from '../components/DangerousWrapper';
import { dotHas } from './index';

/**
 * Formats the total for a particular header value for a table.
 * @param {object} total - An object representing the totals for all columns of a table, keyed by header machine name value.
 * @param {object} header - An object representing a single column from a table.
 * @param {string} header.align - The alignment of the column, set to "left" or "right".
 * @param {string} header.name - A human readable name of the header.
 * @param {string} header.type - The format to use on the total value.
 * @param {string} header.value - The machine name of the header to obtain the total for.
 * @param {number} mantissa - The number of significant digits to ensure are
 *     displayed with the formatted total.
 * @returns {object} - A span tag with the formatted total as its child.
 */
const getTotal = (total, header, mantissa = 0) => {
  let totalValue;
  switch (header.type) {
    case 'currency':
      totalValue = toCurrency(total[header.value], mantissa);
      break;
    case 'currency delta':
      totalValue = toCurrency(total[header.value], mantissa, true);
      break;
    case 'number':
      totalValue = toNumFormat(total[header.value], mantissa);
      break;
    default:
      totalValue = total[header.value];
  }
  return<span className="total-cell">{totalValue}</span>;
};


/**
  * Determines the longest word in a string.
  * @param {string} The sentence string for which you want to find the length of the longest word.
  * @returns {number} The calculated length of the longest string.
  */
const longestWord = (str) => {
  const words = str.split(' ');
  const longer = (champ, contender) => ((contender.length > champ.length) ? contender : champ);
  const word = words.reduce(longer);
  return word.length;
};

/**
 * Calculates the column width to use for a particular column based on the contents in the
 * columns cells, header, and footer and the maximum column width defined by the maxColWidth prop.
 * @param {object[]} rows - An array of objects representing each row in the table.
 * @param {string} accessor - The name of the column to calculate the width for.
 * @param {string} headerText - The displayed header for the column.
 * @param {string} type - Used to help determine how to calculate the maximum width allowed.
 * @param {object[]} total - An array of objects representing the total for the each column in the table.
 * @param {number} maxColWidth - The default maximum width a column can be.
 * @param {number} mantissa - The number of significant digits to ensure are
 *     displayed with the formatted total.
 * @returns {number} The calculated width of the the column in pixels.
 */
export const getColumnWidth = ({
  rows, accessor, headerText, type, total, maxColWidth, mantissa = 0
}) => {
  const maxWidth = maxColWidth || 100;
  let magicSpacing = 9;
  const headerLength = longestWord(headerText) * 0.75; // We weight header text less that the cell content here.
  const footerLength = total && total[accessor] ? total[accessor].toString().length : 0;
  const getMax = () => {
    let max = footerLength > headerLength ? footerLength : headerLength;
    const rowMax = rows.length;
    let i = 0;
    for (; i < rowMax; i += 1) {
      const row = rows[i];
      let value;
      const rowType = (row[accessor] && (row[accessor]).$$typeof) ? 'react' : type;
      if (row[accessor] || row[accessor] === 0) {
        switch (rowType) {
          case 'react':
            /** Renders the component as an HTML string, replaces some special HTML unicode characters with a single character
              * and removes any HTML tags in order to get the count of characters from the rendered string content.
             */
            value = ReactDOMServer.renderToString(row[accessor]).replace(/(<(?:.|\n)*?>)|(\.)/gm, '').replace(/&(#x27;)|(quot;)|(&sect)/gm, '&').length;
            break;
          case 'currency':
            value = (toCurrency(row[accessor], mantissa)).replace(/(<(?:.|\n)*?>)|(\.)/gm, '').length;
            magicSpacing = 10;
            break;
          case 'currency delta':
            value = (toCurrency(row[accessor], mantissa, true)).replace(/(<(?:.|\n)*?>)|(\.)/gm, '').length;
            magicSpacing = 10;
            break;
          case 'number':
            value = (toNumFormat(row[accessor], mantissa)).replace(/(<(?:.|\n)*?>)|(\.)/gm, '').length;
            magicSpacing = 10;
            break;
          case 'html':
            value = (row[accessor]).replace(/(<(?:.|\n)*?>)|(\.)/gm, '').replace(/&(#x27;)|(quot;)|(&sect)/gm, '&').length;
            break;
          default:
            value = (row[accessor]).length;
        }
      } else {
        value = 0;
      }
      max = (value > max) ? value : max;
    }
    return max;
  };
  const cellLength = getMax();
  return Math.min(maxWidth, cellLength * magicSpacing);
};

/**
 * Generates an entire table's column props for the ExpTreeTable or SimpleDataTable components.
 * @param {object} table - Represents data for the table to generate column props for.
 * @param {object[]} table.headers - An array of objects, with each object representing data related to a single column's header.
 * @param {boolean} table.filterable - Whether or not this table column can be filtered.
 * @param {string} table.headers[].align - The alignment of the column, set to "left" or "right".
 * @param {string} table.headers[].name - A human readable name of the header.
 * @param {string} table.headers[].type - The format to use on the total value.
 * @param {string} table.headers[].value - The machine name of the header to obtain the total for.
 * @param {function} table.headers[].filterMethod - Only used if table.filterable is true.
 *     The function to run that determines how column values are filtered against user input.
 *     If not included, filtering is done by seeing if user input is included in lower cased values
 *     within the column.
 * @param {string[]} aggregateBy - An array of machine name column values to aggregate by.
 * @param {boolean} setMaxColWidth - Whether or not a maximum column width should be set for the table.
 * @param {number} maxColWidth - The maximum width permitted for each table column in pixels.
 * @param {number} mantissa - The number of significant digits to ensure are
 *     displayed.
 * @returns {object} An object that represents the column prop for a component.
 */
export const getColumns = (table, {
  aggregateBy, setMaxColWidth, maxColWidth, mantissa = 0
}) => table.headers.map((header) => getHeaderProps({
  table, header, aggregateBy, setMaxColWidth, maxColWidth, mantissa
}));

/**
 * Generates a single column's props to use with the ExpTreeTable or SimpleDataTable components.
 * @param {object} funcProps - An object with properties representing settings for a single column's prop generation.
 * @see getColumns
 * @see {@link https://github.com/tannerlinsley/react-table/tree/v6#columns}
 * @returns {object} - An object that represents a single header row for a component's column prop.
 */
export const getHeaderProps = (funcProps) => {
  const { header } = funcProps;
  let columns = [];
  // Handles nested headers.
  if (header.type === 'headerGroup' && header.headers) {
    const cloneProps = cloneDeep(funcProps);
    columns = header.headers.map((nestedHeader) => {
      cloneProps.header = nestedHeader;
      return getHeaderProps(cloneProps);
    });
  }
  const {
    table, aggregateBy, setMaxColWidth, maxColWidth, mantissa
  } = funcProps;
  // This represents a React Table column prop object.
  // The default value for the column is added from ReactTableDefaults.column before we add overrides.
  const props = {
    ...ReactTableDefaults.column,
    Header: (header.footnote ? (
      <span>
        {header.name}
&nbsp;
        <FootNoteLink index={header.footnote} />
        {' '}
      </span>
    ) : header.name),
    accessor: header.value,
    className: `${header.type} ${header.align}`,
    headerClassName: `${header.type} ${header.align}`,
    // @see {@link https://github.com/tannerlinsley/react-table/tree/v6#custom-cell-header-and-footer-rendering}
    // eslint-disable-next-line react/prop-types
    Cell: (row) => {
      const rowType = (row.value && (row.value).$$typeof) ? 'react' : header.type;
      let rowFormatted;
      switch (rowType) {
        case 'currency':
          rowFormatted = row.value && <DangerousWrapper htmlTag="span" rawHtml={(toCurrency(row.value, mantissa))} />;
          break;
        case 'currency delta':
          rowFormatted = row.value && <DangerousWrapper htmlTag="span" rawHtml={(toCurrency(row.value, mantissa, true))} />;
          break;
        case 'number':
          rowFormatted = row.value && <DangerousWrapper htmlTag="span" rawHtml={(toNumFormat(row.value, mantissa))} />;
          break;
        case 'html':
          rowFormatted = row.value && <DangerousWrapper htmlTag="span" rawHtml={row.value} />;
          break;
        default:
          rowFormatted = row.value;
      }
      /**
      * The following function adds a footnote link to a specific cell given
      * an expected footnote data cell json structure of
      * footnote: [{ field: fieldValue, index: footnoteIndex }, { field: fieldValue, index: footnoteIndex }]
      * */
      if (row.original && row.original.footnote) {
        let k = 0;
        const footnoteLength = row.original.footnote.length;
        for (; k < footnoteLength; k += 1) {
          if (row.original.footnote[k].field === header.value) {
            rowFormatted = (
              <React.Fragment>
                {rowFormatted}
                <FootNoteLink index={row.original.footnote[k].index} />
              </React.Fragment>
            );
          }
        }
      }
      return rowFormatted;
    },
    Footer: (table.total ? getTotal(table.total, header, mantissa) : null),
    minWidth: setMaxColWidth ? getColumnWidth({
      rows: table.data, accessor: header.value, headerText: header.name, type: header.type, total: table.total, maxColWidth, mantissa
    }) : null, // @see {@link https://github.com/tannerlinsley/react-table/tree/v6#pivoting-and-aggregation}
    aggregate: (values) => (aggregateBy && aggregateBy.includes(header.value)) && sum(values)
  };
  if (header.type === 'currency') {
    // @see {@link https://github.com/tannerlinsley/react-table/tree/v6#custom-sorting-algorithm}
    props.sortMethod = (a, b) => {
      // Returns undefined when given a string that can't convert back to a number.
      let newA = numbro.unformat(a);
      let newB = numbro.unformat(b);
      if (typeof newA === 'undefined') {
        newA = null;
      }
      if (typeof newB === 'undefined') {
        newB = null;
      }
      // If they're both numbers, return the difference between the two.
      // If only A Or B are defined, let it sort higher.
      if (newA && newB) {
        return(newA - newB);
      }
      if (newA && !newB) {
        return 1;
      }
      if (!newA && newB) {
        return(-1);
      }
      // Both undefined, they're equal to each other.
      return 0;
    };
  }
  if (table.filterable) {
    if (header.type === 'fuzzy') {
      props.className = `${props.className} string`;
      // Used to render the filter UI of a filter-enabled column.
      // @see {@link https://github.com/tannerlinsley/react-table/tree/v6#renderers}
      props.filterMethod = header.filterMethod ? header.filterMethod : (filter, row) => {
        const escapeRegexCharacters = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/-/g, '').toLowerCase();
        const escapedValue = escapeRegexCharacters(filter.value.trim());
        const regex = new RegExp(`${escapedValue}`, 'i');
        if (escapedValue === '') {
          return false;
        }
        let cell = row[filter.id];
        if (dotHas(row[filter.id], 'props.children')) {
          const { children } = row[filter.id].props;
          if (Array.isArray(children)) {
            cell = children.map((child) => ReactDOMServer.renderToString(child).replace(/<[^>]+>/g, ' ').trim());
            cell = cell.join(' ');
          }
          if (typeof children === 'string') {
            cell = children;
          }
        }
        return regex.test(escapeRegexCharacters(cell));
      };
      /* eslint react/prop-types: 0 */
      props.Filter = ({ filter, onChange }) => (
        <input
          type="text"
          id={`${header.value}-input`}
          placeholder={`Enter a ${(header.name).toLowerCase()}...`}
          style={{
            width: '100%'
          }}
          value={filter ? filter.value : ''}
          onChange={(event) => onChange(event.target.value)}
        />
      );
      /* eslint react/prop-types: 1 */
    } else {
      // Turn off filtering for remaining columns.
      props.filterable = false;
    }
  }
  if (columns.length) {
    props.columns = columns;
  }
  return props;
};

/**
 * Formats a number to a comma separated string
 * with a specific number of significant digits.
 * @param {number} number - The number to format.
 * @param {number} decimal - The number of significant digits to ensure are returned.
 * @returns {*} The newly formatted number or the original value of number if the argument is not a number.
 */
export const toNumFormat = (number, decimal) => {
  if (!Number.isNaN(Number(number)) && (number || number === 0)) {
    const numFormat = numbro(number);
    return numFormat.format({
      mantissa: decimal || 0,
      trimMantissa: false,
      thousandSeparated: true,
      negative: 'parenthesis'
    });
  }
  return number;
};

/**
 * Formats a number to be displayed in USD.
 * @param {number} number - The number to format.
 * @param {number} decimal - The number of significant digits to ensure are returned.
 * @param {boolean} forceSign - Whether to add + sign in front of postivie value.
 * @returns {*} The newly formatted number or the original value of number if the argument is not a number.
 */
export const toCurrency = (number, decimal, forceSign = false) => {
  if (!Number.isNaN(Number(number))) {
    const currency = numbro(number);
    return currency.format({
      mantissa: decimal || 0,
      trimMantissa: false,
      thousandSeparated: true,
      negative: 'parenthesis',
      forceSign
    });
  }
  return number;
};

/**
 * Formats the display of a line item from a line item number.
 * @param {string} account - The line item number to format.
 * @returns {string} The formatted line item number.
 */
export const toLineItem = (account) => `${account.slice(0, 4)}-${account.slice(4)}`;
